import { createListenerMiddleware, isAnyOf } from '@reduxjs/toolkit'
import { RootState } from '@/store/store'
import { AgentEventsActions } from '@/agent/events/store/slice'
import { AgentReferencesActions } from '@/agent/references/store/slice'
import { AgentEventType, ClientAgentEventRead } from '@/agent/events/store/schemas'
import { ClientAgentReference, AgentReferenceType } from '@/agent/references/store/schemas'
import { AnalyticsEvent } from '@/analytics/schema/events.schema'
import { getTrackEventGlobal } from '@/analytics/utils/analytics-helpers'
const riaAnalyticsListener = createListenerMiddleware()
const start = riaAnalyticsListener.startListening

// ============================================================================
// Constants
// ============================================================================

const MAX_EVENT_AGE_MS = 60000 // Maximum age for an event to be considered "new" (60 seconds)
const REFERENCE_IDLE_WAIT_MS = 5000 // Time to wait without new references before tracking (5 seconds)
const MAX_ABSOLUTE_WAIT_MS = 60000 // Maximum absolute time to wait before tracking (60 seconds)
// ============================================================================
// State Management
// ============================================================================

const pendingEvents = new Set<string>()
const eventFirstSeenTime = new Map<string, number>()
const lastReferenceTime = new Map<string, number>()
const pendingTimeouts = new Map<string, NodeJS.Timeout>()
const trackedEvents = new Set<string>()
const trackedEventReferenceCounts = new Map<string, number>()
const eventsBeingTracked = new Set<string>()

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Check if an event is recent (not historical)
 */
function isRecentEvent(event: ClientAgentEventRead): boolean {
  const eventAge = Date.now() - event.created_at
  return eventAge < MAX_EVENT_AGE_MS
}

/**
 * Find an event by ID in the Redux state
 */
function findEventInState(eventId: string, state: RootState): ClientAgentEventRead | undefined {
  for (const conversationId of Object.keys(state.agentEventsState.events)) {
    const events = state.agentEventsState.events[conversationId] || {}
    const event = Object.values(events).find((e) => e?.id === eventId)
    if (event) {
      return event
    }
  }
  return undefined
}

/**
 * Find the conversation ID for an event
 */
function findConversationIdForEvent(eventId: string, state: RootState): string | undefined {
  for (const conversationId of Object.keys(state.agentEventsState.events)) {
    const events = state.agentEventsState.events[conversationId] || {}
    if (Object.values(events).some((e) => e?.id === eventId)) {
      return conversationId
    }
  }
  return undefined
}

/**
 * Get all research references for a specific turn_id
 */
function getResearchReferencesForTurn(turnId: string, state: RootState): ClientAgentReference[] {
  const turnUniqueCitationAndHighlightCount = state.agentReferencesState.turnUniqueCitationAndHighlightCount[turnId] || {}
  const turnUniqueCitationIds = Object.keys(turnUniqueCitationAndHighlightCount)

  const allReferences: ClientAgentReference[] = []
  turnUniqueCitationIds.forEach((citationId) => {
    const ref = state.agentReferencesState.references[citationId]
    if (ref && ref.reference.turn_id === turnId) {
      allReferences.push(ref)
    }
  })

  return allReferences
}

/**
 * Count research references by type (CASELAW and LRR only)
 */
function countResearchReferences(references: ClientAgentReference[]): {
  total: number
  caselaw: number
  lrr: number
} {
  let caselaw = 0
  let lrr = 0

  references.forEach((ref) => {
    if (ref.type === AgentReferenceType.CASELAW) {
      caselaw++
    } else if (ref.type === AgentReferenceType.LRR) {
      lrr++
    }
  })

  return {
    total: caselaw + lrr,
    caselaw,
    lrr,
  }
}

/**
 * Check if tracking conditions are met based on timeouts
 */
function shouldTrackEvent(
  eventId: string,
  now: number
): {
  shouldTrack: boolean
  idleTimeoutReached: boolean
  minWaitReached: boolean
  maxAbsoluteTimeoutReached: boolean
  timeSinceFirstSeen: number
  timeSinceLastReference: number
} {
  const lastRefTime = lastReferenceTime.get(eventId)
  const firstSeen = eventFirstSeenTime.get(eventId) || now

  const timeSinceFirstSeen = now - firstSeen
  const timeSinceLastReference = lastRefTime ? now - lastRefTime : timeSinceFirstSeen

  const idleTimeoutReached = timeSinceLastReference >= REFERENCE_IDLE_WAIT_MS
  const minWaitReached = timeSinceFirstSeen >= REFERENCE_IDLE_WAIT_MS
  const maxAbsoluteTimeoutReached = timeSinceFirstSeen >= MAX_ABSOLUTE_WAIT_MS

  const shouldTrack = (idleTimeoutReached && minWaitReached) || maxAbsoluteTimeoutReached

  return {
    shouldTrack,
    idleTimeoutReached,
    minWaitReached,
    maxAbsoluteTimeoutReached,
    timeSinceFirstSeen,
    timeSinceLastReference,
  }
}

/**
 * Clean up all resources for an event
 */
function cleanupEventResources(eventId: string): void {
  pendingEvents.delete(eventId)
  eventFirstSeenTime.delete(eventId)
  lastReferenceTime.delete(eventId)
  trackedEventReferenceCounts.delete(eventId)
  eventsBeingTracked.delete(eventId)

  const existingTimeout = pendingTimeouts.get(eventId)
  if (existingTimeout) {
    clearTimeout(existingTimeout)
    pendingTimeouts.delete(eventId)
  }
}

/**
 * Calculate timeout duration for tracking, considering maximum absolute timeout
 */
function calculateTimeoutDuration(eventId: string, now: number): number {
  const firstSeenTime = eventFirstSeenTime.get(eventId)
  if (!firstSeenTime) {
    return REFERENCE_IDLE_WAIT_MS
  }

  const timeSinceFirstSeen = now - firstSeenTime
  const timeUntilMaxAbsolute = MAX_ABSOLUTE_WAIT_MS - timeSinceFirstSeen
  const timeUntilMaxAbsoluteMs = Math.max(0, timeUntilMaxAbsolute)

  return Math.min(REFERENCE_IDLE_WAIT_MS, timeUntilMaxAbsoluteMs)
}

/**
 * Schedule a timeout to track an event after the calculated duration
 */
function scheduleTrackingTimeout(eventId: string, now: number, api: { getState: () => unknown }, track: ReturnType<typeof getTrackEventGlobal>): void {
  const timeoutDuration = calculateTimeoutDuration(eventId, now)

  const timeoutId = setTimeout(() => {
    const currentState = api.getState() as RootState
    const foundEvent = findEventInState(eventId, currentState)

    if (foundEvent && foundEvent.type === AgentEventType.AGENT_RESPONSE) {
      processPendingEvent(foundEvent, currentState, track)
    }

    pendingTimeouts.delete(eventId)
  }, timeoutDuration)

  pendingTimeouts.set(eventId, timeoutId)
}

// ============================================================================
// Core Processing Functions
// ============================================================================

/**
 * Process a pending event to check if it should be tracked
 */
function processPendingEvent(event: ClientAgentEventRead, state: RootState, track: ReturnType<typeof getTrackEventGlobal>): void {
  const eventId = event.id

  // Skip if currently being tracked to prevent race conditions
  if (eventsBeingTracked.has(eventId)) {
    return
  }

  // Skip if already tracked
  if (trackedEvents.has(eventId)) {
    return
  }

  // Find conversation ID
  const conversationId = findConversationIdForEvent(eventId, state)
  if (!conversationId) {
    cleanupEventResources(eventId)
    return
  }

  // Get and count research references for this turn
  const allReferences = getResearchReferencesForTurn(event.turn_id, state)
  const counts = countResearchReferences(allReferences)

  // Check if tracking conditions are met
  const now = Date.now()
  const { shouldTrack } = shouldTrackEvent(eventId, now)

  if (!shouldTrack || !track) {
    return
  }

  // Only track if there are research references
  if (counts.total === 0) {
    // No references and timeout passed, clean up
    cleanupEventResources(eventId)
    return
  }

  // Mark as being tracked immediately to prevent duplicate tracking
  eventsBeingTracked.add(eventId)

  try {
    // Track the event
    track(AnalyticsEvent.RiaResponseReceived, {
      conversationId,
      riaMessageId: event.id,
      riaReferencesCount: counts.total,
      riaCaselawReferencesCount: counts.caselaw,
      riaLrrReferencesCount: counts.lrr,
      riaHasResearchResults: true,
    })

    // Mark as tracked and record reference count
    trackedEvents.add(eventId)
    trackedEventReferenceCounts.set(eventId, counts.total)
  } finally {
    // Always clean up tracking state
    eventsBeingTracked.delete(eventId)
    cleanupEventResources(eventId)
  }
}

// ============================================================================
// Event Handlers
// ============================================================================

/**
 * Handle new agent response events
 */
function handleNewEvents(events: ClientAgentEventRead[]): void {
  events.forEach((event) => {
    if (event.type !== AgentEventType.AGENT_RESPONSE) {
      return
    }

    if (!isRecentEvent(event)) {
      return
    }

    // Add to pending events and record first seen time
    pendingEvents.add(event.id)
    eventFirstSeenTime.set(event.id, Date.now())
  })
}

/**
 * Handle new references arriving via WebSocket
 */
function handleNewReferences(references: ClientAgentReference[], api: { getState: () => unknown }, track: ReturnType<typeof getTrackEventGlobal>): void {
  const updatedState = api.getState() as RootState
  const now = Date.now()
  const affectedTurnIds = new Set(references.map((ref) => ref.reference.turn_id))

  // Process all pending events to update last reference time
  const pendingEventIdsSnapshot = Array.from(pendingEvents)
  pendingEventIdsSnapshot.forEach((eventId) => {
    const foundEvent = findEventInState(eventId, updatedState)

    if (!foundEvent) {
      cleanupEventResources(eventId)
      return
    }

    // Update last reference time if this event's turn_id matches
    if (affectedTurnIds.has(foundEvent.turn_id) && foundEvent.type === AgentEventType.AGENT_RESPONSE) {
      // Clear existing timeout since we just got a new reference
      const existingTimeout = pendingTimeouts.get(eventId)
      if (existingTimeout) {
        clearTimeout(existingTimeout)
        pendingTimeouts.delete(eventId)
      }

      // Update last reference time
      lastReferenceTime.set(eventId, now)

      // Schedule new timeout for tracking
      scheduleTrackingTimeout(eventId, now, api, track)
    }
  })
}

/**
 * Check for pending events that have reached their timeout
 */
function checkPendingEventsTimeout(api: { getState: () => unknown }, track: ReturnType<typeof getTrackEventGlobal>): void {
  const currentState = api.getState() as RootState
  const now = Date.now()
  const pendingEventIds = Array.from(pendingEvents)

  pendingEventIds.forEach((eventId) => {
    // Skip if already tracked or being tracked
    if (trackedEvents.has(eventId) || eventsBeingTracked.has(eventId)) {
      return
    }

    const { shouldTrack } = shouldTrackEvent(eventId, now)
    if (!shouldTrack) {
      return
    }

    const foundEvent = findEventInState(eventId, currentState)
    if (foundEvent && foundEvent.type === AgentEventType.AGENT_RESPONSE) {
      processPendingEvent(foundEvent, currentState, track)
    }
  })
}

// ============================================================================
// Redux Listener
// ============================================================================

start({
  matcher: isAnyOf(AgentEventsActions.upsertEvents, AgentReferencesActions.upsertReferences),
  effect: async (action, api) => {
    const track = getTrackEventGlobal()
    if (!track) {
      return
    }

    // Handle new events
    if (AgentEventsActions.upsertEvents.match(action)) {
      handleNewEvents(action.payload.events)
    }

    // Handle new references
    if (AgentReferencesActions.upsertReferences.match(action)) {
      handleNewReferences(action.payload.references, api, track)
    }

    // Check for pending events that have reached timeout (after processing new references)
    checkPendingEventsTimeout(api, track)
  },
})

export default riaAnalyticsListener
